import {
  annualMonthlyPrice,
  PLAN_TIERS,
  PLANS,
  planHasFeature,
  type PlanFeature,
  type PlanTier,
} from '@/shared/config/plans';

/**
 * Content behind the pricing cards and the comparison matrix.
 *
 * Prices and limits are read from `PLANS` — the same matrix the plan gates
 * use and the backend enforces — so marketing copy cannot drift from what a
 * subscription actually unlocks.
 */

export type PlanCardContent = {
  tier: PlanTier;
  monthlyPrice: number;
  /** Price per month when billed annually; equal to monthly for Free. */
  annualPrice: number;
  /** Keys under `marketing:pricing.featureNames`. */
  featureKeys: readonly string[];
  /** Tier whose features this one builds on, shown as "Everything in X, plus:". */
  inheritsFrom?: PlanTier;
  highlighted?: boolean;
  tone: 'outline' | 'gradient' | 'dark';
  surface: 'white' | 'muted';
};

const CARD_COPY: Record<
  PlanTier,
  Omit<PlanCardContent, 'tier' | 'monthlyPrice' | 'annualPrice'>
> = {
  free: {
    featureKeys: ['bookings75', 'photos10', 'calendar', 'telegramMaster'],
    tone: 'outline',
    surface: 'white',
  },
  solo: {
    featureKeys: ['bookings150', 'photos30', 'clientReminders', 'crmNotes'],
    inheritsFrom: 'free',
    tone: 'outline',
    surface: 'white',
  },
  pro: {
    featureKeys: ['unlimitedBookings', 'unlimitedPortfolio', 'analytics', 'premiumCatalog'],
    inheritsFrom: 'solo',
    highlighted: true,
    tone: 'gradient',
    surface: 'white',
  },
  studio: {
    featureKeys: ['team4', 'sharedSchedule', 'rbac', 'customDomain'],
    inheritsFrom: 'pro',
    tone: 'dark',
    surface: 'muted',
  },
};

export const PLAN_CARDS: readonly PlanCardContent[] = PLAN_TIERS.map((tier) => ({
  tier,
  monthlyPrice: PLANS[tier].price.UAH,
  annualPrice: annualMonthlyPrice(tier, 'UAH'),
  ...CARD_COPY[tier],
}));

/** A cell is a tick, a dash, or a value key under `pricing.featureValues`. */
export type ComparisonValue =
  boolean | { valueKey: 'unlimited' | 'upTo' | 'seats'; count?: number };

export type ComparisonRow = {
  /** Key under `marketing:pricing.featureNames`. */
  featureKey: string;
  /** Key under `marketing:pricing.categories`. */
  categoryKey: 'marketing' | 'automation' | 'analytics' | 'team';
  value: (tier: PlanTier) => ComparisonValue;
};

const limit = (count: number | null): ComparisonValue =>
  count === null ? { valueKey: 'unlimited' } : { valueKey: 'upTo', count };
const has = (feature: PlanFeature) => (tier: PlanTier) => planHasFeature(tier, feature);
const always = () => true;

export const COMPARISON_ROWS: readonly ComparisonRow[] = [
  { featureKey: 'profile', categoryKey: 'marketing', value: always },
  {
    featureKey: 'portfolioPhotos',
    categoryKey: 'marketing',
    value: (tier) => limit(PLANS[tier].portfolioPhotos),
  },
  { featureKey: 'premiumCatalog', categoryKey: 'marketing', value: has('catalogBoost') },
  // Replying is reputation work, so it sits with marketing. Hiding a review is
  // not offered on any tier and therefore has no row here.
  { featureKey: 'reviewReplies', categoryKey: 'marketing', value: has('reviewReplies') },
  { featureKey: 'customDomain', categoryKey: 'marketing', value: has('customDomain') },
  { featureKey: 'calendar', categoryKey: 'automation', value: always },
  {
    featureKey: 'bookingsPerMonth',
    categoryKey: 'automation',
    value: (tier) => limit(PLANS[tier].bookingsPerMonth),
  },
  { featureKey: 'telegramMaster', categoryKey: 'automation', value: always },
  { featureKey: 'clientReminders', categoryKey: 'automation', value: has('clientReminders') },
  { featureKey: 'crmNotes', categoryKey: 'analytics', value: has('crmNotes') },
  { featureKey: 'analytics', categoryKey: 'analytics', value: has('analytics') },
  {
    featureKey: 'teamManagement',
    categoryKey: 'team',
    value: (tier) =>
      PLANS[tier].seats > 1 ? { valueKey: 'seats', count: PLANS[tier].seats } : false,
  },
  { featureKey: 'sharedSchedule', categoryKey: 'team', value: has('teamMembers') },
  { featureKey: 'rbac', categoryKey: 'team', value: has('teamMembers') },
];

/** Keys under `marketing:pricing.faq`, in display order. */
export const FAQ_KEYS = ['changePlan', 'commission', 'telegram', 'staff'] as const;
